import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AdminLayout from "@/components/layout/AdminLayout";
import API, { formatPrice, getImageUrl } from "@/lib/api";
import { useLanguage } from "@/contexts/LanguageContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandItem } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { normalizeNumberInput, sanitizeLeadingZeroInput } from "@/lib/utils";
import { Search, ChevronLeft, ChevronRight, Eye, Plus, CreditCard, Download, Printer, Truck, Copy, FileDown } from "lucide-react";

const poPaymentStatusMap = {
  unpaid: { label: "Belum Bayar", color: "bg-amber-100 text-amber-800" },
  partially_paid: { label: "Bayar Sebagian", color: "bg-blue-100 text-blue-800" },
  waiting_verification_dp: { label: "Menunggu Verifikasi DP", color: "bg-blue-600 text-white" },
  dp_paid: { label: "Sudah DP", color: "bg-sky-100 text-sky-800" },
  waiting_verification_settlement: { label: "Menunggu Verifikasi Pelunasan", color: "bg-blue-600 text-white" },
  paid: { label: "Lunas", color: "bg-emerald-100 text-emerald-800" },
  cancelled: { label: "Dibatalkan", color: "bg-red-100 text-red-800" },
};

const readyStockPaymentStatusMap = {
  unpaid: { label: "Menunggu Pembayaran", color: "bg-amber-100 text-amber-800" },
  partially_paid: { label: "Bayar Sebagian", color: "bg-blue-100 text-blue-800" },
  paid: { label: "Lunas", color: "bg-emerald-100 text-emerald-800" },
  cancelled: { label: "Dibatalkan", color: "bg-red-100 text-red-800" },
};

const pendingVerificationMeta = { label: "Menunggu Verifikasi Pembayaran", color: "bg-blue-600 text-white" };

const orderProgressMap = {
  pending: { label: "Menunggu Pembayaran", color: "bg-stone-100 text-stone-700" },
  processing: { label: "Diproses", color: "bg-cyan-100 text-cyan-800" },
  waiting_po_close: { label: "Menunggu PO Close", color: "bg-yellow-100 text-yellow-800" },
  po_closed: { label: "PO Ditutup", color: "bg-orange-100 text-orange-800" },
  sent_to_central: { label: "Dikirim ke Pusat", color: "bg-cyan-100 text-cyan-800" },
  in_production: { label: "Dalam Produksi", color: "bg-indigo-100 text-indigo-800" },
  ready_at_central: { label: "Ready di Pusat", color: "bg-violet-100 text-violet-800" },
  ready_at_gallery: { label: "Ready di Gallery", color: "bg-fuchsia-100 text-fuchsia-800" },
  ready_to_ship: { label: "Siap Kirim", color: "bg-cyan-100 text-cyan-800" },
  shipped: { label: "Dikirim", color: "bg-purple-100 text-purple-800" },
  completed: { label: "Selesai", color: "bg-emerald-600 text-white" },
  cancelled: { label: "Dibatalkan", color: "bg-red-100 text-red-800" },
};

const technicalVariantKeys = new Set(["variant_id", "product_id", "weight_gram", "weight", "sku", "stock", "price", "cost_price", "size_price_adjustment", "sort_order", "status", "source_hash", "created_at", "updated_at", "pricing"]);

const labelValueOrDash = (value) => {
  const text = String(value ?? "").trim();
  return text || "-";
};

const escapeLabelHtml = (value) => String(labelValueOrDash(value))
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#039;");

const chunkLabels = (labels, size = 4) => {
  const pages = [];
  for (let i = 0; i < labels.length; i += size) {
    pages.push(labels.slice(i, i + size));
  }
  return pages;
};

const buildPrintLabelCardHtml = (label) => {
  const items = (label.items || []).map((item) => `
    <div class="item-row">
      <div>${escapeLabelHtml(item.name)}</div>
      <div class="item-qty">x${Number(item.qty || 0)}</div>
    </div>
  `).join("");

  return `
    <article class="label-card">
      <section>
        <div class="recipient-name">${escapeLabelHtml(label.recipient?.name)} <span>(${escapeLabelHtml(label.recipient?.phone)})</span></div>
        <div class="address"><strong>Alamat:</strong><br />${escapeLabelHtml(label.recipient?.address)}</div>
      </section>
      <section>
        <strong>Pengirim:</strong><br />${escapeLabelHtml(label.sender?.name)} (${escapeLabelHtml(label.sender?.phone)})
      </section>
      <div class="divider"></div>
      <section class="shipment">
        <div>${escapeLabelHtml(label.shipment?.courier)} <span>(${escapeLabelHtml(label.shipment?.weight)})</span></div>
        <div class="shipping-cost">Ongkir: ${escapeLabelHtml(label.shipment?.shipping_cost)}</div>
      </section>
      <div class="divider"></div>
      <section><span class="tracking-badge">ID Transaksi: ${escapeLabelHtml(label.transaction?.id)}</span></section>
      <section class="items">${items}</section>
      <section class="footer">📦 Total Item ${Number(label.footer?.total_qty || 0)} pcs</section>
    </article>
  `;
};

const buildPrintLabelsDocument = (labels) => {
  const pagesHtml = chunkLabels(labels, 4).map((pageLabels) => `
    <div class="label-page">
      <div class="label-grid">
        ${pageLabels.map(buildPrintLabelCardHtml).join("")}
      </div>
    </div>
  `).join("");

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Print Label Pengiriman</title>
  <style>
    @page { size: A4 portrait; margin: 25.4mm; }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: #fff; color: #000; font-family: Arial, Helvetica, sans-serif; }
    .label-page { width: 100%; break-after: page; page-break-after: always; }
    .label-page:last-child { break-after: auto; page-break-after: auto; }
    .label-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); grid-template-rows: repeat(2, auto); gap: 3mm; width: 100%; align-items: start; }
    .label-card { width: 100%; border: 2px solid #000; border-radius: 8px; padding: 4mm; overflow: visible; break-inside: avoid; page-break-inside: avoid; font-size: 10pt; line-height: 1.25; }
    .label-card section { margin: 0 0 2.5mm; }
    .recipient-name { font-weight: 700; font-size: 11pt; }
    .recipient-name span { font-weight: 400; }
    .address { margin-top: 1mm; }
    .divider { border-top: 1px solid #000; margin: 0 0 2.5mm; }
    .shipment { font-size: 12pt; font-weight: 900; text-transform: uppercase; line-height: 1.08; }
    .shipping-cost { font-size: 10pt; font-weight: 700; text-transform: none; margin-top: 1mm; }
    .tracking-badge { display: inline-block; background: #000; color: #fff; border-radius: 999px; padding: 1mm 2mm; font-size: 9pt; font-weight: 700; }
    .items { margin-top: 1.5mm; }
    .item-row { display: grid; grid-template-columns: 1fr 12mm; gap: 2mm; margin-bottom: 1.2mm; overflow-wrap: anywhere; font-size: 8.5pt; line-height: 1.15; }
    .item-qty { text-align: right; font-weight: 700; white-space: nowrap; font-size: 8.5pt; }
    .footer { background: #f5f5f4; border-radius: 6px; padding: 1.5mm 2mm; font-weight: 700; }
  </style>
</head>
<body>${pagesHtml}</body>
</html>`;
};

const printLabelsInWindow = (labels) => {
  const printWindow = window.open("", "_blank", "width=900,height=700");
  if (!printWindow) {
    toast.error("Popup print diblokir browser. Izinkan popup untuk halaman ini, lalu coba lagi.");
    return;
  }
  printWindow.document.open();
  printWindow.document.write(buildPrintLabelsDocument(labels));
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
  }, 300);
};

const getDisplayProductLabel = (productName = "", brandName = "") => {
  const rawName = String(productName || "").trim();
  const cleanBrandName = String(brandName || "").trim();
  if (!rawName) return cleanBrandName || "Produk";
  const nameWithoutLegacyBrand = rawName.replace(/\s+by\s+.+$/i, "").trim();
  if (!cleanBrandName || cleanBrandName === "No Brand") return nameWithoutLegacyBrand || rawName;
  return `${nameWithoutLegacyBrand || rawName} by ${cleanBrandName}`;
};

const getDisplayVariantValues = (variant = {}) => {
  return Object.entries(variant || {})
    .filter(([key, value]) => !technicalVariantKeys.has(key) && value !== null && value !== undefined && String(value).trim() !== "")
    .map(([, value]) => String(value));
};

const getVariantLabelWithoutSize = (variant = {}) => {
  const values = Object.entries(variant || {})
    .filter(([key, value]) => key !== "size" && !technicalVariantKeys.has(key) && value !== null && value !== undefined && String(value).trim() !== "")
    .map(([, value]) => String(value).trim())
    .filter((value) => value !== "-");
  return values.join(" / ") || "Varian";
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const deepLinkHandled = useRef(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [progressFilter, setProgressFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [paymentCounts, setPaymentCounts] = useState({ all: 0 });
  const [progressCounts, setProgressCounts] = useState({ all: 0 });
  const itemsPerPage = 20;

  // Selected Order Modal
  // Selected Order Modal
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [installments, setInstallments] = useState([]);
  const [confirmations, setConfirmations] = useState([]);
  const [rekenings, setRekenings] = useState([]);
  const [shippingInput, setShippingInput] = useState({ courier: '', awb: '', notes: '', name: '', phone: '', address: '' });
  const [shippingInputs, setShippingInputs] = useState({});
  const [completingOrder, setCompletingOrder] = useState(false);
  const [newInstallment, setNewInstallment] = useState({ nominal: 0, payment_type: "transfer", notes: "" });


  const [verifyingId, setVerifyingId] = useState(null);
  const [verifyNominal, setVerifyNominal] = useState(0);
  const [editingConfirmationId, setEditingConfirmationId] = useState(null);
  const [editConfirmationNominal, setEditConfirmationNominal] = useState(0);
  const [adminPOEditOpen, setAdminPOEditOpen] = useState(false);
  const [adminPOEditRows, setAdminPOEditRows] = useState([]);
  const [adminPOEditProduct, setAdminPOEditProduct] = useState(null);
  const [adminPOEditReason, setAdminPOEditReason] = useState("");
  const [savingAdminPOEdit, setSavingAdminPOEdit] = useState(false);
  const [orderEditLogs, setOrderEditLogs] = useState([]);
  const [selectedLabelOrderIds, setSelectedLabelOrderIds] = useState([]);
  const [printingSelectedLabels, setPrintingSelectedLabels] = useState(false);
  const fetchOrders = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    params.set("limit", String(itemsPerPage));
    params.set("skip", String((currentPage - 1) * itemsPerPage));
    if (debouncedSearch.trim()) params.set("search", debouncedSearch.trim());
    if (paymentFilter !== "all") params.set("payment_status", paymentFilter);
    if (progressFilter !== "all") params.set("order_status", progressFilter);

    return API.get(`/orders?${params.toString()}`).then((r) => {
      setOrders(r.data.orders || []);
      setTotalOrders(Number(r.data.total || 0));
      setPaymentCounts(r.data.payment_counts || { all: Number(r.data.total || 0) });
      setProgressCounts(r.data.progress_counts || { all: Number(r.data.total || 0) });
    }).catch(() => { }).finally(() => setLoading(false));
  }, [currentPage, debouncedSearch, paymentFilter, progressFilter]);
  const fetchRekenings = () => API.get("/rekenings").then((r) => setRekenings((r.data.rekenings || []).filter((rek) => rek.status === "active"))).catch(() => { });
  useEffect(() => { fetchRekenings(); }, []);
  useEffect(() => { fetchOrders(); }, [fetchOrders]);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Deep-link: auto-open order detail when order_id is in URL
  useEffect(() => {
    if (deepLinkHandled.current || loading || orders.length === 0) return;
    const targetOrderId = searchParams.get("order_id");
    if (targetOrderId) {
      const order = orders.find((o) => o.id === targetOrderId);
      if (order) {
        handleOpenDetails(order);
        deepLinkHandled.current = true;
        searchParams.delete("order_id");
        setSearchParams(searchParams, { replace: true });
      } else {
        API.get(`/orders/${targetOrderId}`).then((res) => {
          handleOpenDetails(res.data);
          deepLinkHandled.current = true;
          searchParams.delete("order_id");
          setSearchParams(searchParams, { replace: true });
        }).catch(() => {});
      }
    }
  }, [loading, orders, searchParams, setSearchParams]);

  const fetchInstallments = (orderId) => {
    API.get(`/installments/${orderId}`).then((r) => setInstallments(r.data.installments)).catch(() => { });
  };

  const fetchConfirmations = (orderId) => {
    API.get(`/orders/${orderId}/confirmations`).then((r) => setConfirmations(r.data.confirmations)).catch(() => { });
  };

  const fetchOrderEditLogs = (orderId) => {
    API.get(`/orders/${orderId}/edit-logs`).then((r) => setOrderEditLogs(r.data.logs || [])).catch(() => setOrderEditLogs([]));
  };

  const getOrderProductNames = (order) => {
    const names = Array.from(new Set((order?.items || []).map((item) => getDisplayProductLabel(item?.product_name || "", item?.brand_name || "").trim()).filter(Boolean)));
    return names;
  };

  const getOrderVariantLines = (order) => {
    const grouped = new Map();
    (order?.items || []).forEach((item) => {
      const variant = item?.variant || {};
      const label = getVariantLabelWithoutSize(variant);
      const sizeValue = variant?.size !== null && variant?.size !== undefined && String(variant.size).trim() !== ""
        ? String(variant.size).trim()
        : "-";
      if (!grouped.has(label)) {
        grouped.set(label, new Set());
      }
      grouped.get(label).add(sizeValue);
    });
    return Array.from(grouped.entries()).map(([label, sizeSet]) => ({
      label,
      sizes: Array.from(sizeSet),
    }));
  };

  const isPreorderOrder = (order) => Boolean(order?.payment_summary?.is_preorder) || (order?.items || []).some((item) => item?.stock_type === "preorder" || item?.product?.stock_type === "preorder");
  const getPaymentStatusMeta = (code, order = null) => {
    if (order?.has_pending_confirmation) return pendingVerificationMeta;
    const map = isPreorderOrder(order) ? poPaymentStatusMap : readyStockPaymentStatusMap;
    return map[code] || { label: code || "-", color: "bg-stone-100 text-stone-700" };
  };
  const getOrderProgressMeta = (code, order = null) => {
    if (!isPreorderOrder(order) && order?.has_pending_confirmation) return pendingVerificationMeta;
    return orderProgressMap[code] || { label: code || "-", color: "bg-stone-100 text-stone-700" };
  };

  const paymentFilterOptions = [
    { value: "all", label: "Semua Pembayaran" },
    { value: "unpaid", label: "Belum Bayar" },
    { value: "partially_paid", label: "Bayar Sebagian" },
    { value: "waiting_verification_dp", label: "Menunggu Verifikasi DP" },
    { value: "dp_paid", label: "Sudah DP" },
    { value: "waiting_verification_settlement", label: "Menunggu Verifikasi Pelunasan" },
    { value: "paid", label: "Lunas" },
    { value: "cancelled", label: "Dibatalkan" },
  ];

  const progressFilterOptions = [
    { value: "all", label: "Semua Pesanan" },
    { value: "waiting_po_close", label: "Menunggu PO Close" },
    { value: "po_closed", label: "PO Ditutup" },
    { value: "sent_to_central", label: "Dikirim ke Pusat" },
    { value: "in_production", label: "Dalam Produksi" },
    { value: "ready_at_central", label: "Ready di Pusat" },
    { value: "ready_at_gallery", label: "Ready di Gallery" },
    { value: "ready_to_ship", label: "Siap Kirim" },
    { value: "shipped", label: "Dikirim" },
    { value: "completed", label: "Selesai" },
    { value: "cancelled", label: "Dibatalkan" },
  ];

  const filteredOrders = orders;
  const totalPages = Math.max(1, Math.ceil(totalOrders / itemsPerPage));
  const paginatedOrders = orders;

  useEffect(() => { setCurrentPage(1); }, [debouncedSearch, paymentFilter, progressFilter]);
  useEffect(() => { setSelectedLabelOrderIds([]); }, [currentPage, debouncedSearch, paymentFilter, progressFilter]);

  const toggleLabelOrder = (orderId) => {
    setSelectedLabelOrderIds((prev) => prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]);
  };

  const toggleCurrentPageLabels = () => {
    const pageIds = paginatedOrders.map((order) => order.id).filter(Boolean);
    if (!pageIds.length) return;
    setSelectedLabelOrderIds((prev) => {
      const current = new Set(prev);
      const allSelected = pageIds.every((id) => current.has(id));
      if (allSelected) pageIds.forEach((id) => current.delete(id));
      else pageIds.forEach((id) => current.add(id));
      return Array.from(current);
    });
  };

  const handlePrintSelectedLabels = async () => {
    if (!selectedLabelOrderIds.length || printingSelectedLabels) return;
    if (selectedLabelOrderIds.length > 40) {
      toast.error("Maksimal 40 transaksi sekali print label.");
      return;
    }
    setPrintingSelectedLabels(true);
    try {
      const res = await API.post("/admin/labels/preview", { transaction_ids: selectedLabelOrderIds });
      const labels = res.data.labels || [];
      if (!labels.length) {
        toast.error("Tidak ada label yang bisa dicetak dari transaksi terpilih.");
        return;
      }
      if (res.data.has_missing_data) {
        toast.warning("Beberapa transaksi belum punya data label lengkap. Data kosong akan dicetak sebagai '-' .");
      }
      printLabelsInWindow(labels);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal membuat label dari transaksi terpilih");
    } finally {
      setPrintingSelectedLabels(false);
    }
  };

  const getVariantOptionLabel = (variant = {}) => {
    const values = [variant.style, variant.color, variant.size].filter((v) => v !== null && v !== undefined && String(v).trim() !== "").map((v) => String(v).trim());
    return values.join(" / ") || variant.sku || variant.variant_id || "Varian";
  };

  const isAdminPOEditAllowed = (order) => {
    if (!order || !isPreorderOrder(order)) return false;
    if (order.has_pending_confirmation) return false;
    const paymentStatus = String(order.payment_status_v2 || "");
    const orderStatus = String(order.order_status_v2 || order.status_shipping || "");
    if (["waiting_verification_dp", "waiting_verification_settlement", "cancelled"].includes(paymentStatus)) return false;
    if (["ready_to_ship", "shipped", "completed", "cancelled"].includes(orderStatus)) return false;
    if ((order.shipments || []).length > 0) return false;
    if ((order.items || []).some((item) => item.shipment_id)) return false;
    return true;
  };

  const openAdminPOEdit = async () => {
    if (!selectedOrder || !isAdminPOEditAllowed(selectedOrder)) return;
    const productIds = Array.from(new Set((selectedOrder.items || []).map((item) => item.product_id).filter(Boolean)));
    if (productIds.length !== 1) {
      toast.error("Editor admin saat ini hanya mendukung 1 produk PO per transaksi");
      return;
    }
    try {
      const res = await API.get(`/products/${productIds[0]}`);
      setAdminPOEditProduct(res.data);
      setAdminPOEditRows((selectedOrder.items || []).map((item) => ({
        transaction_detail_id: item.transaction_detail_id,
        variant: item.variant || {},
        quantity: Number(item.quantity || 1),
        item_notes: item.item_notes || "",
      })));
      setAdminPOEditReason("");
      setAdminPOEditOpen(true);
    } catch (err) {
      toast.error("Gagal mengambil data produk PO");
    }
  };

  const updateAdminPOEditRow = (idx, patch) => {
    setAdminPOEditRows((rows) => rows.map((row, i) => i === idx ? { ...row, ...patch } : row));
  };

  const addAdminPOEditRow = () => {
    const firstVariant = (adminPOEditProduct?.variants || [])[0] || {};
    setAdminPOEditRows((rows) => [...rows, { transaction_detail_id: null, variant: firstVariant, quantity: 1, item_notes: "" }]);
  };

  const removeAdminPOEditRow = (idx) => {
    setAdminPOEditRows((rows) => rows.length <= 1 ? rows : rows.filter((_, i) => i !== idx));
  };

  const saveAdminPOEdit = async () => {
    const reason = adminPOEditReason.trim();
    if (reason.length < 5) {
      toast.error("Alasan edit wajib diisi minimal 5 karakter");
      return;
    }
    if (!window.confirm("Simpan edit item transaksi secara permanen? Total dan tagihan akan dihitung ulang.")) return;
    setSavingAdminPOEdit(true);
    try {
      const payload = {
        admin_reason: reason,
        items: adminPOEditRows.map((row) => ({
          transaction_detail_id: row.transaction_detail_id || null,
          variant: row.variant || {},
          quantity: Math.max(1, Number(row.quantity || 1)),
          item_notes: row.item_notes || "",
        })),
      };
      const res = await API.put(`/orders/${selectedOrder.id}/items/revise`, payload);
      setSelectedOrder(res.data);
      setAdminPOEditOpen(false);
      fetchOrders();
      fetchInstallments(selectedOrder.id);
      fetchOrderEditLogs(selectedOrder.id);
      toast.success("Item transaksi berhasil diedit dan audit log tersimpan");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan edit transaksi");
    } finally {
      setSavingAdminPOEdit(false);
    }
  };

  const updateStatus = async (orderId, status) => {
    try {
      await API.put(`/orders/${orderId}/status`, { status });
      toast.success(t("common.saved") || "Status updated!");
      fetchOrders();
    } catch { toast.error("Failed to update"); }
  };

  const handleOpenDetails = (order) => {
    setSelectedOrder(order);
    setAdminPOEditOpen(false);
    fetchInstallments(order.id);
    fetchConfirmations(order.id);
    fetchOrderEditLogs(order.id);
    let shipping = {};
    if (order.shipping && typeof order.shipping === "object") {
      shipping = order.shipping;
    } else if (order.shipping && typeof order.shipping === "string") {
      try { shipping = JSON.parse(order.shipping); } catch { }
    }
    setShippingInput({
      courier: shipping.courier || '',
      awb: shipping.awb || '',
      notes: shipping.shipping_notes || '',
      name: shipping.name || '',
      phone: shipping.phone || '',
      address: shipping.address || ''
    });

    if (order.shipments && order.shipments.length > 0) {
      const sInputs = {};
      order.shipments.forEach(s => {
        sInputs[s.id] = {
          courier: s.courier || '',
          awb: s.awb || '',
          notes: s.shipping_notes || '',
          name: s.name || '',
          phone: s.phone || '',
          address: s.address || '',
          sender_mode: s.sender_type || (s.sender_name || s.sender_phone ? 'dropshipper' : 'reseller'),
          sender_name: s.sender_name || order.user_name || '',
          sender_phone: s.sender_phone || order.user_phone || '',
          sender_address: ''
        };
      });
      setShippingInputs(sInputs);
    }
  };

  const handleUpdateShipping = async (shipment_id = null, markShipped = true) => {
    try {
      let payload = shipment_id ? shippingInputs[shipment_id] : shippingInput;
      if (shipment_id) {
        const shipmentPayload = { ...payload, shipment_id, sender_address: '', sender_type: payload.sender_mode || 'reseller', mark_shipped: markShipped };
        delete shipmentPayload.sender_mode;
        payload = shipmentPayload;
      }

      if (!shipment_id) payload = { ...payload, mark_shipped: markShipped };
      await API.put(`/orders/${selectedOrder.id}/shipping-receipt`, payload);
      toast.success(markShipped ? (t("admin_orders.modal.shipping_success") || "Resi pengiriman berhasil disimpan") : "Data pengiriman berhasil disimpan");
      fetchOrders();
      // Refetch selected order to update shipments state
      const res = await API.get(`/orders/${selectedOrder.id}`);
      setSelectedOrder(res.data);
    } catch (err) {
      toast.error("Gagal menyimpan resi");
    }
  };

  const handleCompleteOrder = async () => {
    if (!selectedOrder) return;
    if (!window.confirm("Tandai pesanan ini selesai?")) return;
    setCompletingOrder(true);
    try {
      const res = await API.post(`/orders/${selectedOrder.id}/complete`);
      toast.success("Pesanan ditandai selesai");
      fetchOrders();
      setSelectedOrder(res.data || { ...selectedOrder, order_status_v2: "completed", status_shipping: "completed" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyelesaikan pesanan");
    } finally {
      setCompletingOrder(false);
    }
  };

  const getOrderUrl = (orderId) => {
    const origin = typeof window !== "undefined" && window.location?.origin ? window.location.origin : "https://haniyahgallery.com";
    return `${origin}/orders/${orderId}`;
  };

  const formatCopyPrice = (value) => {
    const amount = Math.round(Number(value || 0));
    return `Rp ${amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")}`;
  };

  const formatWIBDateTime = (value) => {
    if (!value) return "-";
    const raw = String(value);
    const date = new Date(raw.endsWith("Z") || raw.includes("+") ? raw : `${raw}Z`);
    if (Number.isNaN(date.getTime())) return raw.slice(0, 16).replace("T", " ");
    return new Intl.DateTimeFormat("id-ID", {
      timeZone: "Asia/Jakarta",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date).replace(".", ":") + " WIB";
  };

  const cleanCopyText = (value) => String(value ?? "")
    .replace(/[\u00A0\u202F]/g, " ")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "")
    .trim();

  const getBillMeta = (order) => {
    const ps = order?.payment_summary || {};
    const total = Number(ps.grand_total ?? order?.grand_total ?? order?.total ?? order?.total_price ?? 0);
    const paid = Number(ps.total_paid ?? order?.total_paid ?? 0);
    const outstanding = Math.max(0, Number(ps.outstanding_total ?? (total - paid) ?? 0));
    const dueAmount = Number(ps.due_now_amount || outstanding || 0);
    let dueLabel = "Tagihan";
    if (ps.due_now_type === "dp") dueLabel = "Tagihan DP";
    if (ps.due_now_type === "settlement") dueLabel = "Tagihan Pelunasan";
    if (ps.due_now_type === "full") dueLabel = "Tagihan Pembayaran";
    if (!ps.due_now_type && outstanding <= 0) dueLabel = "Status Pembayaran";
    return { total, paid, outstanding, dueAmount, dueLabel };
  };

  const getCustomerFirstName = (order) => {
    const rawName = order?.user_name || order?.shipping?.name || order?.user_email || "kak";
    const firstName = String(rawName).split("@")[0].trim().split(/\s+/)[0];
    return firstName || "kak";
  };

  const getOrderTitleForBill = (order) => {
    const firstItem = (order?.items || [])[0] || {};
    return getDisplayProductLabel(firstItem?.product_name || "Order", firstItem?.brand_name || "");
  };

  const getItemNameForBill = (item) => {
    const variantText = getDisplayVariantValues(item?.variant || {}).join(" ").trim();
    if (variantText) return variantText;
    return getDisplayProductLabel(item?.product_name || "Produk", item?.brand_name || "");
  };

  const getBillRekeningBlock = () => {
    const activeRekenings = (rekenings || []).filter((rek) => rek.status === "active");
    if (activeRekenings.length === 0) return ["-"];
    const rekeningLines = activeRekenings.map((rek) => {
      const bank = cleanCopyText(rek.bank_name || "");
      const number = cleanCopyText(rek.account_number || "");
      return [bank, number].filter(Boolean).join(" - ");
    }).filter(Boolean);
    const holders = Array.from(new Set(activeRekenings.map((rek) => cleanCopyText(rek.account_holder || "")).filter(Boolean)));
    return [
      ...rekeningLines,
      ...(holders.length > 0 ? ["", `a.n. ${holders.join(" / ")}`] : []),
    ];
  };

  const buildBillText = (order) => {
    const { total, paid, outstanding, dueAmount, dueLabel } = getBillMeta(order);
    const customerName = getCustomerFirstName(order);
    const orderTitle = getOrderTitleForBill(order);
    const orderUrl = getOrderUrl(order?.id || order?.transaction_id);
    const normalTotal = (order?.items || []).reduce((sum, item) => {
      const qty = Number(item.quantity || item.qty || 0) || 1;
      return sum + ((Number(item.price || 0) + Number(item.discount_snapshot || 0)) * qty);
    }, 0);
    const totalDiscount = Math.max(0, normalTotal - total);
    const discountPercent = normalTotal > 0 && totalDiscount > 0 ? Math.round((totalDiscount / normalTotal) * 100) : 0;
    const itemLines = (order?.items || []).map((item) => {
      const qty = Number(item.quantity || item.qty || 0) || 1;
      const unitPrice = Number(item.price || 0) + Number(item.discount_snapshot || 0);
      return `- ${cleanCopyText(getItemNameForBill(item))} : ${formatCopyPrice(unitPrice)} x ${qty}`;
    });
    const billingLabel = dueLabel === "Tagihan" || dueLabel === "Status Pembayaran" ? "Tagihan" : dueLabel;

    return [
      `*Hi ${cleanCopyText(customerName)},*`,
      "Segera lakukan pembayaran untuk orderan berikut:",
      "",
      "===============================",
      "",
      cleanCopyText(orderTitle),
      ...(itemLines.length > 0 ? itemLines : ["- Produk : Rp 0 x 1"]),
      "",
      `Total : ${formatCopyPrice(normalTotal || total)}`,
      `Diskon : ${totalDiscount > 0 ? `${discountPercent > 0 ? `${discountPercent}% / ` : ""}${formatCopyPrice(totalDiscount)}` : "-"}`,
      `Total setelah diskon : ${formatCopyPrice(total)}`,
      `${billingLabel} : ${formatCopyPrice(dueAmount > 0 ? dueAmount : outstanding || total)}`,
      `Yang sudah dibayar: ${formatCopyPrice(paid)}`,
      `Sisa: ${formatCopyPrice(outstanding)}`,
      "",
      "================================",
      "",
      "Pembayaran dapat ditransfer ke:",
      "",
      ...getBillRekeningBlock(),
      "",
      "Silakan upload bukti transfer ke link",
      cleanCopyText(orderUrl),
      "",
      `_Berikut total pesanan ${cleanCopyText(orderTitle)}. Wajib payment DP sebelum close PO dan disetorkan ke Pusat, sebagai fixsasi order dan menghindari terjadinya cancel order sepihak^^_`,
    ].map(cleanCopyText).join("\n");
  };

  const handleCopyBill = async (order) => {
    try {
      await navigator.clipboard.writeText(buildBillText(order));
      toast.success("Tagihan berhasil disalin");
    } catch {
      toast.error("Gagal menyalin tagihan");
    }
  };

  const handleDownloadInvoice = async (order) => {
    const orderId = order?.id || order?.transaction_id;
    if (!orderId) return;
    try {
      const res = await API.get(`/orders/${orderId}/invoice-pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Gagal download invoice");
    }
  };

  const buildDetailShippingLabel = (ship = {}) => {
    const draft = shippingInputs[ship.id] || {};
    const service = [draft.courier || ship.courier, draft.shipping_service || ship.shipping_service].filter(Boolean).join(' · ') || '-';
    const scopedItems = (selectedOrder?.items || []).filter((item) => !ship.id || item.shipment_id === ship.id);
    const items = (scopedItems.length ? scopedItems : selectedOrder?.items || []).map((item) => {
      const variantText = getDisplayVariantValues(item.variant || {}).join(', ');
      return {
        name: `${getDisplayProductLabel(item.product_name, item.brand_name) || '-'}${variantText ? ` - ${variantText}` : ''}`,
        qty: Number(item.quantity || item.qty || 0),
      };
    });
    const totalQty = items.reduce((sum, item) => sum + Number(item.qty || 0), 0);
    return {
      id: `${selectedOrder?.id || selectedOrder?.transaction_id || ''}:${ship.id || 'detail'}`,
      transaction_id: selectedOrder?.id || selectedOrder?.transaction_id || '',
      shipment_id: ship.id || '',
      recipient: {
        name: draft.name || ship.name || selectedOrder?.shipping?.name || selectedOrder?.user_name || '-',
        phone: draft.phone || ship.phone || selectedOrder?.shipping?.phone || selectedOrder?.user_phone || '-',
        address: draft.address || ship.address || selectedOrder?.shipping?.address || selectedOrder?.user_address || '-',
      },
      sender: {
        name: draft.sender_name || ship.sender_name || selectedOrder?.user_name || '-',
        phone: draft.sender_phone || ship.sender_phone || selectedOrder?.user_phone || '-',
      },
      shipment: {
        courier: service,
        weight: ship.shipping_weight_grams ? `${Number(ship.shipping_weight_grams)} gr` : '-',
        shipping_cost: Number(ship.shipping_cost || 0) > 0 ? formatPrice(Number(ship.shipping_cost || 0)) : '-',
        tracking_number: draft.awb || ship.awb || selectedOrder?.id || selectedOrder?.transaction_id || '-',
      },
      transaction: {
        id: selectedOrder?.id || selectedOrder?.transaction_id || '-',
        tracking_number: draft.awb || ship.awb || '-',
      },
      items: items.length ? items : [{ name: '-', qty: 0 }],
      footer: { total_qty: totalQty },
    };
  };

  const handlePrintShippingLabel = async (ship = {}) => {
    const orderId = selectedOrder?.id || selectedOrder?.transaction_id;
    if (!orderId) return;
    try {
      if (ship.id) {
        const res = await API.post("/admin/labels/preview", { transaction_ids: [orderId] });
        const labels = res.data.labels || [];
        const scopedLabel = labels.find((label) => label.shipment_id === ship.id) || labels[0];
        if (scopedLabel) {
          if (scopedLabel.missing_fields?.length) {
            toast.warning("Data label belum lengkap. Data kosong akan dicetak sebagai '-' .");
          }
          printLabelsInWindow([scopedLabel]);
          return;
        }
      }
      printLabelsInWindow([buildDetailShippingLabel(ship)]);
    } catch {
      printLabelsInWindow([buildDetailShippingLabel(ship)]);
    }
  };

  const handleAddInstallment = async () => {
    if (newInstallment.nominal <= 0) {
      toast.error("Nominal must be greater than 0");
      return;
    }
    try {
      await API.post("/installments", { ...newInstallment, transaction_id: selectedOrder.id });
      toast.success(t("admin_orders.modal.success_add_payment"));
      setNewInstallment({ nominal: 0, payment_type: "transfer", notes: "" });
      fetchInstallments(selectedOrder.id);
      fetchOrders(); // refresh order status in case it became paid
    } catch {
      toast.error("Failed to add installment");
    }
  };

  const handleVerifyConfirmation = async (cid) => {
    if (verifyNominal <= 0) {
      toast.error("Nominal must be greater than 0");
      return;
    }
    try {
      await API.post(`/admin/confirmations/${cid}/verify`, { nominal: verifyNominal });
      toast.success("Payment verified!");
      setVerifyingId(null);
      setVerifyNominal(0);
      fetchInstallments(selectedOrder.id);
      fetchConfirmations(selectedOrder.id);
      fetchOrders();
    } catch {
      toast.error("Failed to verify payment");
    }
  };

  const handleRejectConfirmation = async (cid) => {
    if (!window.confirm("Reject this payment proof?")) return;
    try {
      await API.post(`/admin/confirmations/${cid}/reject`);
      toast.success("Payment rejected");
      fetchConfirmations(selectedOrder.id);
    } catch {
      toast.error("Failed to reject payment");
    }
  };

  const handleUpdateConfirmationNominal = async (cid) => {
    const nominal = normalizeNumberInput(editConfirmationNominal, 0);
    if (nominal <= 0) {
      toast.error("Nominal must be greater than 0");
      return;
    }
    try {
      await API.patch(`/admin/confirmations/${cid}/nominal`, { nominal });
      toast.success("Nominal verifikasi berhasil diupdate");
      setEditingConfirmationId(null);
      setEditConfirmationNominal(0);
      fetchInstallments(selectedOrder.id);
      fetchConfirmations(selectedOrder.id);
      fetchOrders();
    } catch {
      toast.error("Failed to update verified nominal");
    }
  };

  const totalPaid = installments.reduce((sum, inst) => sum + inst.nominal, 0);
  const totalDiscount = selectedOrder
    ? (selectedOrder.items || []).reduce((sum, item) => sum + (Number(item.discount_snapshot || 0) * Number(item.quantity || 0)), 0)
    : 0;
  const subtotalBeforeDiscount = selectedOrder
    ? (selectedOrder.items || []).reduce((sum, item) => sum + ((Number(item.price || 0) + Number(item.discount_snapshot || 0)) * Number(item.quantity || 0)), 0)
    : 0;
  const itemsSubtotal = selectedOrder
    ? (selectedOrder.items || []).reduce((sum, item) => sum + Number(item.item_total || 0), 0)
    : 0;
  const shipmentShippingCost = selectedOrder
    ? (selectedOrder.shipments || []).reduce((sum, ship) => sum + Number(ship.shipping_cost || 0), 0)
    : 0;
  const orderShippingCost = selectedOrder
    ? Number(selectedOrder.shipping?.shipping_cost ?? shipmentShippingCost ?? 0)
    : 0;
  const remaining = selectedOrder ? Math.max(0, selectedOrder.grand_total - totalPaid) : 0;
  const paymentSummary = selectedOrder?.payment_summary || {};
  const isPreorder = Boolean(paymentSummary.is_preorder);
  const dpTarget = Number(paymentSummary.dp_target || 0);
  const dpRemaining = Math.max(0, dpTarget - totalPaid);
  const settlementDue = Number(paymentSummary.settlement_due || 0);

  return (
    <AdminLayout title={t("admin_orders.table.transaction") + " (PO)"}>
      <div data-testid="admin-orders">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <p className="text-sm text-stone-500 whitespace-nowrap">{totalOrders} {t("admin_orders.total_transactions")}</p>
            <button
              onClick={handlePrintSelectedLabels}
              disabled={!selectedLabelOrderIds.length || printingSelectedLabels}
              className="flex items-center gap-2 px-3 py-1.5 bg-stone-900 text-white text-[11px] font-bold rounded hover:bg-stone-800 transition-all duration-200 uppercase tracking-wider shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Printer className="w-3.5 h-3.5" />
              {printingSelectedLabels ? "Menyiapkan..." : `Print Label Terpilih${selectedLabelOrderIds.length ? ` (${selectedLabelOrderIds.length})` : ""}`}
            </button>
            <button
              onClick={() => {
                const token = localStorage.getItem("token");
                window.open(`${API.defaults.baseURL}/admin/orders/export-by-product?token=${token}`, '_blank');
              }}
              className="flex items-center gap-2 px-3 py-1.5 bg-[#8A9A86] text-white text-[11px] font-bold rounded hover:bg-[#7a8a76] transition-all duration-200 uppercase tracking-wider shadow-sm hover:shadow-md"
            >
              <Download className="w-3.5 h-3.5" />
              {t("admin_orders.export_per_product")}
            </button>
            <button
              onClick={() => navigate('/admin/orders/create')}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-[#8A9A86] text-white text-[11px] font-bold rounded hover:bg-[#7a8a76] transition-all duration-200 uppercase tracking-wider shadow-sm hover:shadow-md"
            >
              <Plus className="w-3.5 h-3.5" />
              Tambah Order
            </button>
          </div>
          <div className="relative w-full sm:w-64">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-stone-400" />
            </div>
            <Input
              type="text"
              placeholder={t("admin_orders.search_placeholder_v2")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 h-9 border-stone-200 focus:ring-stone-400"
            />
          </div>
        </div>

        <div className="space-y-3 mb-6">
          <div>
            <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-2">Filter Pembayaran</p>
            <div className="flex flex-wrap gap-2">
              {paymentFilterOptions.map((option) => {
                const active = paymentFilter === option.value;
                return (
                  <button
                    key={`payment-${option.value}`}
                    onClick={() => setPaymentFilter(option.value)}
                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border text-sm font-medium transition-colors ${active ? "border-[#8A9A86] bg-[#8A9A86] text-white" : "border-stone-200 bg-white text-stone-600 hover:bg-stone-50"}`}
                  >
                    <span>{option.label}</span>
                    <span className={`inline-flex min-w-6 justify-center rounded-full px-1.5 py-0.5 text-xs ${active ? "bg-white/20 text-white" : "bg-stone-100 text-stone-600"}`}>
                      {paymentCounts[option.value] || 0}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-stone-500 font-semibold mb-2">Filter Pesanan</p>
            <div className="flex flex-wrap gap-2">
              {progressFilterOptions.map((option) => {
                const active = progressFilter === option.value;
                return (
                  <button
                    key={`progress-${option.value}`}
                    onClick={() => setProgressFilter(option.value)}
                    className={`inline-flex items-center gap-2 px-3 py-2 rounded-full border text-sm font-medium transition-colors ${active ? "border-[#8A9A86] bg-[#8A9A86] text-white" : "border-stone-200 bg-white text-stone-600 hover:bg-stone-50"}`}
                  >
                    <span>{option.label}</span>
                    <span className={`inline-flex min-w-6 justify-center rounded-full px-1.5 py-0.5 text-xs ${active ? "bg-white/20 text-white" : "bg-stone-100 text-stone-600"}`}>
                      {progressCounts[option.value] || 0}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="bg-white border border-stone-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b border-stone-100 bg-stone-50/50">
                <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold w-10">
                  <input
                    type="checkbox"
                    checked={paginatedOrders.length > 0 && paginatedOrders.every((order) => selectedLabelOrderIds.includes(order.id))}
                    onChange={toggleCurrentPageLabels}
                    aria-label="Pilih semua transaksi di halaman ini"
                    className="h-4 w-4 rounded border-stone-300 accent-stone-900"
                  />
                </th>
                <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold">{t("admin_orders.table.transaction")}</th>
                <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold">{t("admin_orders.table.customer")}</th>
                <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold">Nama Produk</th>
                <th className="text-right p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold">{t("admin_orders.table.total")}</th>
                <th className="text-right p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold">{t("admin_orders.table.total_paid")}</th>
                <th className="text-left p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold">{t("admin_orders.table.status")}</th>
                <th className="text-right p-3 text-xs uppercase tracking-wider text-stone-500 font-semibold">{t("admin_orders.table.actions")}</th>
              </tr></thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={8} className="p-8 text-center text-stone-400">{t("common.loading")}</td></tr>
                ) : paginatedOrders.length === 0 ? (
                  <tr><td colSpan={8} className="p-8 text-center text-stone-400">{t("admin_orders.no_orders")}</td></tr>
                ) : paginatedOrders.map((o) => {
                  const productNames = getOrderProductNames(o);
                  const primaryProduct = getDisplayProductLabel(productNames[0] || "-", o.items?.[0]?.brand_name || "");
                  const variantLines = getOrderVariantLines(o);
                  const paymentMeta = getPaymentStatusMeta(o.payment_status_v2, o);
                  const progressMeta = getOrderProgressMeta(o.order_status_v2, o);
                  return (
                  <tr key={o.id} className="border-b border-stone-50">
                    <td className="p-3 align-top">
                      <input
                        type="checkbox"
                        checked={selectedLabelOrderIds.includes(o.id)}
                        onChange={() => toggleLabelOrder(o.id)}
                        aria-label={`Pilih transaksi ${o.id} untuk print label`}
                        className="h-4 w-4 rounded border-stone-300 accent-stone-900"
                      />
                    </td>
                    <td className="p-3">
                      <p className="text-xs font-semibold tracking-wide text-stone-700 break-all">{o.id}</p>
                      <p className="text-xs text-stone-400">{new Date(o.transaction_date || o.created_at).toLocaleDateString()}</p>
                    </td>
                    <td className="p-3">
                      <p className="text-stone-700">{o.shipping?.name || o.user_name || o.user_id}</p>
                      <p className="text-xs text-stone-400">{t("admin_orders.items_count").replace("{count}", o.items?.length || 0)}</p>
                    </td>
                    <td className="p-3 max-w-[320px] align-top">
                      <p className="text-stone-700 font-medium" title={productNames.join(", ") || "-"}>{primaryProduct}</p>
                      <div className="mt-1 space-y-0.5">
                        {variantLines.length > 0 ? variantLines.map((line, idx) => (
                          <p key={`${line.label}-${idx}`} className="text-xs text-stone-400 break-words" title={`${line.label}: [${line.sizes.join(", ")}]`}>
                            {line.label}: [{line.sizes.join(", ")}]
                          </p>
                        )) : <p className="text-xs text-stone-400">-</p>}
                      </div>
                    </td>
                    <td className="p-3 font-medium text-stone-900 text-right">{formatPrice(o.total)}</td>
                    <td className="p-3 font-medium text-stone-600 text-right">{formatPrice(o.total_paid || 0)}</td>
                    <td className="p-3">
                      <div className="flex flex-col items-start gap-1.5">
                        <Badge className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md shadow-none ${paymentMeta.color}`}>
                          Bayar: {paymentMeta.label}
                        </Badge>
                        <Badge className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md shadow-none ${progressMeta.color}`}>
                          Pesanan: {progressMeta.label}
                        </Badge>
                        <p className="text-[10px] text-stone-400 uppercase tracking-wider">Engine: {o.status_payment || o.status}</p>
                      </div>
                    </td>
                    <td className="p-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => handleCopyBill(o)} className="text-stone-500 hover:text-stone-900 p-2" title="Copy tagihan">
                          <Copy className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleDownloadInvoice(o)} className="text-stone-500 hover:text-stone-900 p-2" title="Download invoice">
                          <FileDown className="w-4 h-4" />
                        </button>
                        <button onClick={() => handleOpenDetails(o)} className="text-stone-500 hover:text-stone-900 p-2" title="Detail order">
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )})}
              </tbody>
            </table>
          </div>
        </div>

        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-stone-100 pt-4 mt-6">
            <p className="text-sm text-stone-500">{t("common.page")} {currentPage} {t("common.of")} {totalPages}</p>
            <div className="flex items-start gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 border border-stone-200 rounded text-stone-500 hover:bg-stone-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-1.5 border border-stone-200 rounded text-stone-500 hover:bg-stone-50 disabled:opacity-50"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        <Dialog open={!!selectedOrder} onOpenChange={(open) => !open && setSelectedOrder(null)}>
          <DialogContent className="bg-white w-[calc(100vw-1rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            {selectedOrder && (
              <>
                <DialogHeader className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 text-left">
                  <DialogTitle className="font-serif text-lg sm:text-xl flex items-center gap-2 flex-wrap leading-tight">
                    {t("admin_orders.table.transaction")} <span className="font-sans text-xs sm:text-sm font-semibold tracking-wide text-stone-700 break-all">{selectedOrder.id}</span>
                    <Badge className={`${getPaymentStatusMeta(selectedOrder.payment_status_v2, selectedOrder).color} hover:bg-opacity-80 text-[10px] uppercase shadow-none px-2.5 py-1`}>
                      Bayar: {getPaymentStatusMeta(selectedOrder.payment_status_v2, selectedOrder).label}
                    </Badge>
                    <Badge className={`${getOrderProgressMeta(selectedOrder.order_status_v2, selectedOrder).color} hover:bg-opacity-80 text-[10px] uppercase shadow-none px-2.5 py-1`}>
                      Pesanan: {getOrderProgressMeta(selectedOrder.order_status_v2, selectedOrder).label}
                    </Badge>
                  </DialogTitle>
                  <div className="flex flex-wrap items-center gap-2 self-start sm:self-center">
                    <button onClick={() => handleCopyBill(selectedOrder)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-stone-100 text-stone-700 hover:bg-stone-200 text-xs font-semibold transition-colors">
                      <Copy className="w-3.5 h-3.5" /> Copy Tagihan
                    </button>
                    <button onClick={() => handleDownloadInvoice(selectedOrder)} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-stone-900 text-white hover:bg-stone-800 text-xs font-semibold transition-colors">
                      <FileDown className="w-3.5 h-3.5" /> Invoice
                    </button>
                    <a href={`/orders/${selectedOrder.id}`} target="_blank" rel="noreferrer" className="text-xs text-[#8A9A86] hover:underline font-semibold inline-flex items-center gap-1">
                      Lihat di Storefront <ChevronRight className="w-3 h-3" />
                    </a>
                  </div>
                </DialogHeader>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 mt-4">
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">{t("admin_orders.modal.customer_info")}</h4>
                    <div className="bg-stone-50 p-4 rounded-xl text-sm space-y-2 border border-stone-100">
                      <div className="flex items-start gap-2 flex-wrap">
                        <p className="font-medium text-stone-800 break-words min-w-0">{selectedOrder.user_name || selectedOrder.shipping?.name || selectedOrder.user_id}</p>
                        <Badge className={`${selectedOrder.user_role === "admin" ? "bg-red-50 text-red-700" : selectedOrder.user_role === "reseller" ? "bg-blue-50 text-blue-700" : selectedOrder.user_role === "agen" ? "bg-amber-50 text-amber-700" : ["mitra", "produsen"].includes(selectedOrder.user_role) ? "bg-purple-50 text-purple-700" : "bg-stone-100 text-stone-700"} hover:bg-opacity-80 text-[10px] uppercase shadow-none px-2 py-0.5`}>
                          {selectedOrder.user_role === "produsen" ? "mitra" : selectedOrder.user_role || "retail"}
                        </Badge>
                      </div>
                      <div className="space-y-1.5 text-[13px] leading-relaxed">
                        <p className="text-stone-600 break-all">{selectedOrder.user_email || selectedOrder.user_id}</p>
                        <p className="text-stone-600">{selectedOrder.user_phone || "-"}</p>
                        <p className="text-stone-500 break-words">{selectedOrder.user_address || "-"}</p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">{t("admin_orders.modal.summary")}</h4>
                    <div className="bg-stone-50 p-4 rounded-xl text-sm space-y-2 border border-stone-100">
                      <div className="flex flex-col sm:flex-row sm:justify-between gap-1"><span className="text-stone-500">{t("admin_orders.modal.date")}</span><span className="text-stone-800 sm:text-right">{new Date(selectedOrder.transaction_date).toLocaleString()}</span></div>
                      <div className="flex items-start justify-between gap-3"><span className="text-stone-500">Status Pembayaran</span><Badge className={`${getPaymentStatusMeta(selectedOrder.payment_status_v2, selectedOrder).color} hover:bg-opacity-80 text-[10px] uppercase shadow-none px-2 py-0.5 text-right`}>{getPaymentStatusMeta(selectedOrder.payment_status_v2, selectedOrder).label}</Badge></div>
                      <div className="flex items-start justify-between gap-3"><span className="text-stone-500">Status Pesanan</span><Badge className={`${getOrderProgressMeta(selectedOrder.order_status_v2, selectedOrder).color} hover:bg-opacity-80 text-[10px] uppercase shadow-none px-2 py-0.5 text-right`}>{getOrderProgressMeta(selectedOrder.order_status_v2, selectedOrder).label}</Badge></div>
                      <div className="flex items-start justify-between gap-3"><span className="text-stone-500">Engine Status</span><span className="text-stone-800 uppercase text-xs text-right break-all">{selectedOrder.status_payment}</span></div>
                      <div className="flex items-start justify-between gap-3"><span className="text-stone-500">{t("admin_orders.modal.shipping_status")}</span><span className="text-stone-800 text-right break-words">{selectedOrder.status_shipping}</span></div>
                      {totalDiscount > 0 && (
                        <>
                          <div className="flex justify-between"><span className="text-stone-500">Subtotal Normal</span><span className="text-stone-400 line-through">{formatPrice(subtotalBeforeDiscount)}</span></div>
                          <div className="flex justify-between"><span className="text-stone-500">Total Diskon</span><span className="text-rose-500 font-medium">-{formatPrice(totalDiscount)}</span></div>
                          <div className="flex justify-between"><span className="text-stone-500">Subtotal Setelah Diskon</span><span className="text-stone-800">{formatPrice(itemsSubtotal)}</span></div>
                        </>
                      )}
                      <div className="flex justify-between"><span className="text-stone-500">Ongkir</span><span className={orderShippingCost > 0 ? "text-stone-800" : "text-stone-400"}>{orderShippingCost > 0 ? formatPrice(orderShippingCost) : "-"}</span></div>
                      <div className="flex justify-between pt-2 border-t border-stone-200 font-medium">
                        <span className="text-stone-800">{t("admin_orders.modal.grand_total")}</span>
                        <span className="text-emerald-600">{formatPrice(selectedOrder.grand_total)}</span>
                      </div>
                      {isPreorder && (
                        <>
                          <div className="flex justify-between"><span className="text-stone-500">Target DP</span><span className="text-stone-800 font-medium">{formatPrice(dpTarget)}</span></div>
                          <div className="flex justify-between"><span className="text-stone-500">Sudah Masuk</span><span className="text-stone-800">{formatPrice(totalPaid)}</span></div>
                          <div className="flex justify-between"><span className="text-stone-500">Sisa ke Target DP</span><span className={`${dpRemaining <= 0 ? "text-emerald-600" : "text-amber-600"} font-medium`}>{formatPrice(dpRemaining)}</span></div>
                          {settlementDue > 0 && (
                            <div className="flex justify-between"><span className="text-stone-500">Sisa Pelunasan</span><span className="text-stone-800">{formatPrice(settlementDue)}</span></div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {selectedOrder.order_status_v2 === "shipped" && (
                  <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <h4 className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-1">Selesaikan Pesanan</h4>
                      <p className="text-sm text-emerald-800">Paket sudah dikirim. Tandai selesai jika barang sudah diterima / transaksi selesai.</p>
                    </div>
                    <button
                      onClick={handleCompleteOrder}
                      disabled={completingOrder}
                      className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-xs font-bold uppercase tracking-wider hover:bg-emerald-700 disabled:opacity-60"
                    >
                      {completingOrder ? "Menyimpan..." : "Tandai Selesai"}
                    </button>
                  </div>
                )}

                {selectedOrder.customer_notes && (
                  <div className="mt-4 bg-amber-50/50 border border-amber-100 rounded p-4">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-1">{t("admin_orders.modal.customer_notes") || "Customer Notes"}</h4>
                    <p className="text-sm text-stone-700">{selectedOrder.customer_notes}</p>
                  </div>
                )}

                {/* Shipping Input Block */}
                {(["processing", "shipped", "completed", "po_processing", "po_waiting_settlement", "po_paid_full", "po_shipping", "po_completed"].includes(selectedOrder.status)) && (
                  <div className="mt-4 bg-[#8A9A86]/5 border border-[#8A9A86]/20 rounded p-4">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-[#8A9A86] mb-3">{t("admin_orders.modal.shipping_settings") || "Pengaturan Pengiriman"}</h4>

                    {selectedOrder.shipments && selectedOrder.shipments.length > 0 ? (
                      <div className="space-y-4 border-t border-[#8A9A86]/20 pt-3">
                        {selectedOrder.shipments.map((ship, idx) => (
                          <div key={ship.id} className="bg-white border border-stone-200 p-3 rounded shadow-sm">
                            <div className="flex justify-between items-center mb-2 pb-2 border-b border-stone-100">
                              <p className="text-xs font-semibold text-stone-700">Paket #{idx + 1} &rarr; <span className="text-[#8A9A86]">{ship.name}</span></p>
                              {ship.status === 'shipped' && <span className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded uppercase font-bold">Shipped</span>}
                            </div>
                            <div className="grid grid-cols-2 gap-3 mb-3 border-b border-stone-100 pb-3">
                              <div>
                                <Label className="text-xs text-stone-500">Nama Penerima</Label>
                                <Input value={shippingInputs[ship.id]?.name || ''} onChange={(e) => setShippingInputs({ ...shippingInputs, [ship.id]: { ...shippingInputs[ship.id], name: e.target.value } })} className="mt-1 h-8 text-xs" />
                              </div>
                              <div>
                                <Label className="text-xs text-stone-500">No. HP</Label>
                                <Input value={shippingInputs[ship.id]?.phone || ''} onChange={(e) => setShippingInputs({ ...shippingInputs, [ship.id]: { ...shippingInputs[ship.id], phone: e.target.value } })} className="mt-1 h-8 text-xs" />
                              </div>
                              <div className="col-span-2">
                                <Label className="text-xs text-stone-500">Alamat Lengkap</Label>
                                <Input value={shippingInputs[ship.id]?.address || ''} onChange={(e) => setShippingInputs({ ...shippingInputs, [ship.id]: { ...shippingInputs[ship.id], address: e.target.value } })} className="mt-1 h-8 text-xs" />
                              </div>
                            </div>

                            <div className="mb-3 p-3 bg-amber-50/40 rounded border border-amber-100">
                              <div className="flex items-start justify-between gap-3 mb-3">
                                <div>
                                  <p className="text-[10px] font-semibold text-amber-700 uppercase tracking-wider">Data Pengirim</p>
                                  <p className="text-xs text-stone-600 mt-1">
                                    {shippingInputs[ship.id]?.sender_mode === 'dropshipper' ? "Send As (Dropshipper)" : "Pengirim: Reseller"}
                                  </p>
                                </div>
                                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${shippingInputs[ship.id]?.sender_mode === 'dropshipper' ? "bg-amber-100 text-amber-700" : "bg-stone-100 text-stone-600"}`}>
                                  {shippingInputs[ship.id]?.sender_mode === 'dropshipper' ? "Dropship" : "Reseller"}
                                </span>
                              </div>
                              <div className="grid grid-cols-2 gap-3">
                                <div>
                                  <Label className="text-xs text-stone-500">Nama Pengirim</Label>
                                  <Input autoComplete="off" value={shippingInputs[ship.id]?.sender_name || ''} onChange={(e) => setShippingInputs({ ...shippingInputs, [ship.id]: { ...shippingInputs[ship.id], sender_name: e.target.value } })} placeholder={shippingInputs[ship.id]?.sender_mode === 'dropshipper' ? "Nama dropshipper" : "Nama reseller"} className="mt-1 h-8 text-xs bg-white" />
                                </div>
                                <div>
                                  <Label className="text-xs text-stone-500">Telepon Pengirim</Label>
                                  <Input autoComplete="off" value={shippingInputs[ship.id]?.sender_phone || ''} onChange={(e) => setShippingInputs({ ...shippingInputs, [ship.id]: { ...shippingInputs[ship.id], sender_phone: e.target.value } })} placeholder={shippingInputs[ship.id]?.sender_mode === 'dropshipper' ? "No HP dropshipper" : "No HP reseller"} className="mt-1 h-8 text-xs bg-white" />
                                </div>
                              </div>
                            </div>

                            <div className="mb-3 p-3 bg-stone-50 rounded border border-stone-100">
                              <p className="text-[10px] font-semibold text-stone-400 uppercase tracking-wider mb-2">Produk dalam paket ini:</p>
                              <ul className="text-xs text-stone-600 space-y-1 ml-4 list-disc marker:text-stone-300">
                                {selectedOrder.items?.filter(i => i.shipment_id === ship.id).map(i => (
                                  <li key={i.transaction_detail_id || i.product_id}>
                                    {getDisplayProductLabel(i.product_name, i.brand_name)}
                                    {i.variant && Object.keys(i.variant).length > 0 ? ` (${getDisplayVariantValues(i.variant).join(', ')})` : ""}
                                    <span className="font-semibold text-stone-800 ml-1 normal-case">× {i.quantity || i.qty}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                            {(Number(ship.shipping_cost || 0) > 0 || ship.shipping_service || ship.destination_label) && (
                              <div className="mb-3 p-3 bg-emerald-50/60 rounded border border-emerald-100 text-xs">
                                <p className="text-[10px] font-semibold text-emerald-700 uppercase tracking-wider mb-2">Info Ongkir</p>
                                <div className="space-y-1 text-stone-700">
                                  {ship.destination_label && <div className="flex justify-between gap-3"><span className="text-stone-500">Tujuan</span><span className="text-right font-medium">{ship.destination_label}</span></div>}
                                  {(ship.courier || ship.shipping_service) && <div className="flex justify-between gap-3"><span className="text-stone-500">Service</span><span className="text-right font-medium">{[ship.courier, ship.shipping_service].filter(Boolean).join(" · ")}</span></div>}
                                  {ship.shipping_etd && <div className="flex justify-between gap-3"><span className="text-stone-500">ETD</span><span className="text-right">{ship.shipping_etd}</span></div>}
                                  {Number(ship.shipping_weight_grams || 0) > 0 && <div className="flex justify-between gap-3"><span className="text-stone-500">Berat</span><span className="text-right">{Number(ship.shipping_weight_grams).toLocaleString("id-ID")} gr</span></div>}
                                  <div className="flex justify-between gap-3 pt-1 border-t border-emerald-100 font-semibold"><span className="text-stone-700">Ongkir</span><span className="text-emerald-700">{formatPrice(ship.shipping_cost || 0)}</span></div>
                                </div>
                              </div>
                            )}
                            <div className="mb-3">
                              <Label className="text-xs text-stone-500">{t("admin_orders.modal.awb") || "Nomor Resi / AWB"}</Label>
                              <Input value={shippingInputs[ship.id]?.awb || ''} onChange={(e) => setShippingInputs({ ...shippingInputs, [ship.id]: { ...shippingInputs[ship.id], awb: e.target.value } })} className="mt-1 h-8 text-xs" />
                            </div>
                            <div className="mb-3">
                              <Label className="text-xs text-stone-500">{t("admin_orders.modal.shipping_notes") || "Catatan Pengiriman"}</Label>
                              <Input value={shippingInputs[ship.id]?.notes || ''} onChange={(e) => setShippingInputs({ ...shippingInputs, [ship.id]: { ...shippingInputs[ship.id], notes: e.target.value } })} className="mt-1 h-8 text-xs" />
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <button onClick={() => handleUpdateShipping(ship.id, false)} className="bg-stone-100 text-stone-700 py-1.5 rounded text-xs font-bold hover:bg-stone-200 transition-colors shadow-sm">Save</button>
                              <button onClick={() => handlePrintShippingLabel(ship)} className="bg-white border border-stone-200 text-stone-700 py-1.5 rounded text-xs font-bold hover:bg-stone-50 transition-colors shadow-sm flex items-center justify-center gap-1"><Printer className="w-3 h-3" /> Print Label</button>
                              <button onClick={() => handleUpdateShipping(ship.id, true)} className="bg-[#8A9A86] text-white py-1.5 rounded text-xs font-bold hover:bg-[#768573] transition-colors shadow-sm flex items-center justify-center gap-1"><Truck className="w-3 h-3" /> Ship Order</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 gap-3 mb-3 border-b border-[#8A9A86]/20 pb-3">
                          <div>
                            <Label className="text-xs text-stone-500">Nama Penerima</Label>
                            <Input value={shippingInput.name} onChange={(e) => setShippingInput({ ...shippingInput, name: e.target.value })} className="mt-1" />
                          </div>
                          <div>
                            <Label className="text-xs text-stone-500">No. HP</Label>
                            <Input value={shippingInput.phone} onChange={(e) => setShippingInput({ ...shippingInput, phone: e.target.value })} className="mt-1" />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs text-stone-500">Alamat Lengkap</Label>
                            <Input value={shippingInput.address} onChange={(e) => setShippingInput({ ...shippingInput, address: e.target.value })} className="mt-1" />
                          </div>
                        </div>

                        <div className="mb-3">
                          <Label className="text-xs text-stone-500">{t("admin_orders.modal.awb") || "Nomor Resi / AWB"}</Label>
                          <Input value={shippingInput.awb} onChange={(e) => setShippingInput({ ...shippingInput, awb: e.target.value })} className="mt-1" />
                        </div>
                        <div className="mb-3">
                          <Label className="text-xs text-stone-500">{t("admin_orders.modal.shipping_notes") || "Catatan Pengiriman"}</Label>
                          <Input value={shippingInput.notes} onChange={(e) => setShippingInput({ ...shippingInput, notes: e.target.value })} className="mt-1" />
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <button
                            onClick={() => handleUpdateShipping(null, false)}
                            className="bg-stone-100 text-stone-700 py-2 rounded text-sm font-semibold hover:bg-stone-200 transition-colors shadow-sm"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => handlePrintShippingLabel({ id: null, name: shippingInput.name, phone: shippingInput.phone, address: shippingInput.address, courier: shippingInput.courier, awb: shippingInput.awb })}
                            className="bg-white border border-stone-200 text-stone-700 py-2 rounded text-sm font-semibold hover:bg-stone-50 transition-colors shadow-sm flex items-center justify-center gap-1"
                          >
                            <Printer className="w-4 h-4" /> Print Label
                          </button>
                          <button
                            onClick={() => handleUpdateShipping(null, true)}
                            className="bg-[#8A9A86] text-white py-2 rounded text-sm font-semibold hover:bg-[#768573] transition-colors shadow-sm flex items-center justify-center gap-1"
                          >
                            <Truck className="w-4 h-4" /> Ship Order
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                <div className="mt-6">
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-stone-500">{t("admin_orders.modal.items_purchased")}</h4>
                    {isAdminPOEditAllowed(selectedOrder) && (
                      <button type="button" onClick={openAdminPOEdit} className="px-3 py-1.5 rounded bg-amber-100 text-amber-800 text-[11px] font-bold uppercase tracking-wider hover:bg-amber-200">
                        Edit Item Admin
                      </button>
                    )}
                  </div>
                  {adminPOEditOpen && (
                    <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-3">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-amber-800">Admin Override Edit Transaksi</p>
                        <p className="text-xs text-amber-700 mt-1">Perubahan ini permanen, memakai harga live produk/varian, menghitung ulang total/tagihan, dan menyimpan audit log.</p>
                      </div>
                      <div className="space-y-2">
                        {adminPOEditRows.map((row, idx) => {
                          const selectedVariantId = row.variant?.variant_id || "";
                          return (
                            <div key={idx} className="grid grid-cols-1 md:grid-cols-12 gap-2 bg-white border border-amber-100 rounded-lg p-3">
                              <div className="md:col-span-5">
                                <Label className="text-[11px] text-stone-500">Varian</Label>
                                <select
                                  value={selectedVariantId}
                                  onChange={(e) => {
                                    const variant = (adminPOEditProduct?.variants || []).find((v) => String(v.variant_id || "") === e.target.value) || row.variant || {};
                                    updateAdminPOEditRow(idx, { variant });
                                  }}
                                  className="mt-1 w-full rounded-md border border-stone-200 px-3 py-2 text-sm bg-white"
                                >
                                  {(adminPOEditProduct?.variants || []).map((variant) => (
                                    <option key={variant.variant_id || getVariantOptionLabel(variant)} value={variant.variant_id || ""}>{getVariantOptionLabel(variant)}</option>
                                  ))}
                                </select>
                              </div>
                              <div className="md:col-span-2">
                                <Label className="text-[11px] text-stone-500">Qty</Label>
                                <Input
                                  type="text"
                                  inputMode="numeric"
                                  value={row.quantity ?? ""}
                                  onChange={(e) => updateAdminPOEditRow(idx, { quantity: e.target.value.replace(/[^0-9]/g, "") })}
                                  onBlur={(e) => updateAdminPOEditRow(idx, { quantity: Math.max(1, Number(e.target.value || 1)) })}
                                  className="mt-1"
                                />
                              </div>
                              <div className="md:col-span-4">
                                <Label className="text-[11px] text-stone-500">Catatan Item</Label>
                                <Input value={row.item_notes || ""} onChange={(e) => updateAdminPOEditRow(idx, { item_notes: e.target.value })} className="mt-1" />
                              </div>
                              <div className="md:col-span-1 flex items-end">
                                <button type="button" onClick={() => removeAdminPOEditRow(idx)} disabled={adminPOEditRows.length <= 1} className="w-full px-2 py-2 rounded border border-red-200 text-red-600 text-xs disabled:opacity-40">Hapus</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <button type="button" onClick={addAdminPOEditRow} className="px-3 py-2 rounded border border-amber-200 bg-white text-amber-800 text-xs font-semibold">+ Tambah Varian</button>
                      <div>
                        <Label className="text-xs text-stone-600">Alasan edit admin</Label>
                        <Input value={adminPOEditReason} onChange={(e) => setAdminPOEditReason(e.target.value)} placeholder="Contoh: Koreksi size customer setelah PO close" className="mt-1" />
                      </div>
                      <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => setAdminPOEditOpen(false)} className="px-4 py-2 rounded bg-white border border-stone-200 text-sm font-semibold">Batal</button>
                        <button type="button" onClick={saveAdminPOEdit} disabled={savingAdminPOEdit} className="px-4 py-2 rounded bg-amber-700 text-white text-sm font-semibold disabled:opacity-50">{savingAdminPOEdit ? "Menyimpan..." : "Simpan Edit Admin"}</button>
                      </div>
                    </div>
                  )}
                  <div className="border border-stone-100 rounded overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-stone-50/50 text-stone-500 text-xs uppercase tracking-wider">
                        <tr><th className="p-2 text-left">{t("admin_products.product")}</th><th className="p-2 text-right">{t("admin_products.price")}</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Subtotal</th></tr>
                      </thead>
                      <tbody>
                        {selectedOrder.items?.map((item, i) => (
                          <tr key={i} className="border-t border-stone-50">
                            <td className="p-2">
                              <p className="font-medium text-stone-800">
                                {getDisplayProductLabel(item.product_name, item.brand_name)}
                              </p>
                              {item.variant && Object.keys(item.variant).length > 0 && (
                                <p className="text-xs text-stone-400">{getDisplayVariantValues(item.variant).join(', ')}</p>
                              )}
                              {Number(item.variant?.size_price_adjustment || 0) > 0 && (
                                <p className="text-xs text-stone-500 mt-0.5">Tambahan size: <span className="font-semibold">+{formatPrice(item.variant.size_price_adjustment)}</span></p>
                              )}
                              {item.item_notes && (
                                <p className="text-xs text-amber-600 italic mt-0.5">📝 {item.item_notes}</p>
                              )}
                            </td>
                            <td className="p-2 text-right">
                              {Number(item.discount_snapshot || 0) > 0 ? (
                                <div className="space-y-0.5">
                                  <p className="text-[11px] text-stone-400 line-through">
                                    {formatPrice(Number(item.price || 0) + Number(item.discount_snapshot || 0))}
                                  </p>
                                  <p className="text-sm font-medium text-stone-800">{formatPrice(item.price)}</p>
                                  <p className="text-[11px] text-rose-500 font-medium">
                                    (-{Math.round((Number(item.discount_snapshot || 0) / Math.max(Number(item.price || 0) + Number(item.discount_snapshot || 0), 1)) * 100)}%)
                                  </p>
                                </div>
                              ) : (
                                <span className="text-stone-600">{formatPrice(item.price)}</span>
                              )}
                            </td>
                            <td className="p-2 text-right text-stone-600">{item.quantity}</td>
                            <td className="p-2 text-right">
                              {Number(item.discount_snapshot || 0) > 0 ? (
                                <div className="space-y-0.5">
                                  <p className="text-[11px] text-stone-400 line-through">
                                    {formatPrice((Number(item.price || 0) + Number(item.discount_snapshot || 0)) * Number(item.quantity || 1))}
                                  </p>
                                  <p className="font-medium text-stone-800">{formatPrice(item.item_total)}</p>
                                </div>
                              ) : (
                                <span className="font-medium text-stone-800">{formatPrice(item.item_total)}</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 border-stone-200 bg-stone-50/60">
                          <td colSpan={3} className="p-2 text-right text-xs font-semibold uppercase tracking-wider text-stone-500">Subtotal</td>
                          <td className="p-2 text-right">
                            {totalDiscount > 0 ? (
                              <div className="space-y-0.5">
                                <p className="text-[11px] text-stone-400 line-through">{formatPrice(subtotalBeforeDiscount)}</p>
                                <p className="font-semibold text-stone-900">{formatPrice(itemsSubtotal)}</p>
                                <p className="text-[11px] text-rose-500 font-medium">Diskon total -{formatPrice(totalDiscount)}</p>
                              </div>
                            ) : (
                              <span className="font-semibold text-stone-900">{formatPrice(itemsSubtotal)}</span>
                            )}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                  {orderEditLogs.length > 0 && (
                    <div className="mt-4 rounded-lg border border-stone-200 bg-stone-50 p-3">
                      <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">Riwayat Edit Admin</p>
                      <div className="space-y-2">
                        {orderEditLogs.map((log) => (
                          <div key={log.id} className="rounded bg-white border border-stone-100 p-2 text-xs text-stone-600">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <span className="font-semibold text-stone-800">{log.actor_name || log.actor_user_id || "Admin"}</span>
                              <span>{formatWIBDateTime(log.created_at)}</span>
                            </div>
                            <p className="mt-1">Alasan: <span className="font-medium text-stone-800">{log.reason || "-"}</span></p>
                            <p className="mt-1">Total: <span className="font-medium">{formatPrice(log.total_before || 0)}</span> → <span className="font-medium">{formatPrice(log.total_after || 0)}</span></p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6 border-t border-stone-100 pt-6">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-3">{t("admin_orders.modal.installments")}</h4>

                  {/* Pending Confirmations Section */}
                  {confirmations.filter(c => c.status === 'pending').map((conf) => (
                    <div key={conf.id} className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <p className="text-xs font-bold text-amber-800 uppercase flex items-center gap-2">
                            <CreditCard className="w-3 h-3" /> Pending Confirmation: {conf.payment_type}
                          </p>
                          <p className="text-[10px] text-amber-600 font-mono mt-0.5">{conf.id} • {new Date(conf.created_at).toLocaleString()}</p>
                        </div>
                        <Badge className="bg-amber-200 text-amber-800 hover:bg-amber-200 border-none">Action Required</Badge>
                      </div>

                      <div className="flex gap-4 items-start">
                        <div className="w-24 h-32 bg-stone-200 rounded-md overflow-hidden shrink-0 border border-stone-300">
                          <img
                            src={getImageUrl(conf.proof_url)}
                            alt="Proof"
                            className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => window.open(getImageUrl(conf.proof_url), '_blank')}
                          />
                        </div>
                        <div className="flex-1 space-y-3">
                          {conf.notes && <p className="text-xs text-stone-600 bg-white/50 p-2 rounded italic font-serif">"{conf.notes}"</p>}

                          {verifyingId === conf.id ? (
                            <div className="space-y-2 bg-white/50 p-3 rounded-md border border-amber-100">
                              <Label className="text-[10px] font-bold text-stone-500 uppercase">Input Recieved Nominal</Label>
                              <Input
                                type="number"
                                value={verifyNominal ?? ""}
                                onChange={(e) => setVerifyNominal(sanitizeLeadingZeroInput(e.target.value))}
                                onBlur={(e) => setVerifyNominal(normalizeNumberInput(e.target.value, 0))}
                                className="h-8 text-sm"
                                autoFocus
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleVerifyConfirmation(conf.id)}
                                  className="flex-1 bg-emerald-600 text-white text-[10px] font-bold py-1.5 rounded uppercase hover:bg-emerald-700 transition-colors"
                                >
                                  Verify & Add
                                </button>
                                <button
                                  onClick={() => { setVerifyingId(null); setVerifyNominal(0); }}
                                  className="flex-1 bg-stone-200 text-stone-600 text-[10px] font-bold py-1.5 rounded uppercase hover:bg-stone-300 transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  setVerifyingId(conf.id);
                                  // Accurate DP calculation
                                  let sug = 0;
                                  if (conf.payment_type === 'dp') {
                                    sug = selectedOrder.items.reduce((sum, item) => {
                                      const pct = item.dp_percentage || 50;
                                      return sum + (item.item_total * (pct / 100));
                                    }, 0);
                                  } else {
                                    sug = remaining;
                                  }
                                  setVerifyNominal(sug);
                                }}
                                className="flex-1 bg-emerald-600 text-white text-[10px] font-bold py-2 rounded uppercase hover:bg-emerald-700 transition-colors shadow-sm"
                              >
                                Approve
                              </button>
                              <button
                                onClick={() => handleRejectConfirmation(conf.id)}
                                className="flex-1 bg-rose-100 text-rose-700 text-[10px] font-bold py-2 rounded uppercase hover:bg-rose-200 transition-colors"
                              >
                                Reject
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}

                  {confirmations.filter(c => c.status === 'verified').length > 0 && (
                    <div className="space-y-2 mb-4">
                      <h5 className="text-[10px] font-bold text-stone-500 uppercase tracking-wider">Verified Payment Proofs</h5>
                      {confirmations.filter(c => c.status === 'verified').map((conf) => (
                        <div key={conf.id} className="p-3 border border-emerald-100 rounded bg-emerald-50/50 text-sm">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                            <div>
                              <p className="font-medium text-stone-800">
                                {formatPrice(conf.nominal)}
                                <span className="text-xs font-normal text-emerald-600 ml-2 capitalize rounded bg-emerald-100 px-1.5 py-0.5">{conf.payment_type}</span>
                              </p>
                              <p className="text-xs text-stone-500">{conf.id} • {conf.verified_at ? new Date(conf.verified_at).toLocaleString() : 'Verified'}</p>
                            </div>
                            {editingConfirmationId === conf.id ? (
                              <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                                <Input
                                  type="number"
                                  value={editConfirmationNominal ?? ""}
                                  onChange={(e) => setEditConfirmationNominal(sanitizeLeadingZeroInput(e.target.value))}
                                  onBlur={(e) => setEditConfirmationNominal(normalizeNumberInput(e.target.value, 0))}
                                  className="h-8 text-sm sm:w-36"
                                  autoFocus
                                />
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleUpdateConfirmationNominal(conf.id)}
                                    className="px-3 py-1.5 bg-emerald-600 text-white text-[10px] font-bold rounded uppercase hover:bg-emerald-700 transition-colors"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => { setEditingConfirmationId(null); setEditConfirmationNominal(0); }}
                                    className="px-3 py-1.5 bg-stone-200 text-stone-600 text-[10px] font-bold rounded uppercase hover:bg-stone-300 transition-colors"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() => {
                                  setEditingConfirmationId(conf.id);
                                  setEditConfirmationNominal(Number(conf.nominal || 0));
                                }}
                                className="px-3 py-1.5 bg-white border border-emerald-200 text-emerald-700 text-[10px] font-bold rounded uppercase hover:bg-emerald-50 transition-colors"
                              >
                                Edit Nominal
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="border-t border-stone-100 my-4"></div>

                  {installments.length > 0 ? (
                    <div className="space-y-2 mb-4">
                      {installments.map((inst, i) => (
                        <div key={i} className="flex justify-between items-center p-3 border border-stone-100 rounded bg-stone-50/50 text-sm">
                          <div>
                            <p className="font-medium text-stone-800">{formatPrice(inst.nominal)} <span className="text-xs font-normal text-emerald-600 ml-2 capitalize rounded bg-emerald-100 px-1.5 py-0.5">{inst.payment_type}</span></p>
                            <p className="text-xs text-stone-500">{new Date(inst.installment_date).toLocaleString()} • {inst.notes || 'No notes'}</p>
                          </div>
                          {inst.payment_proof && (
                            <a href={getImageUrl(inst.payment_proof)} target="_blank" rel="noreferrer" className="text-xs text-[#8A9A86] hover:underline flex items-center gap-1"><CreditCard className="w-3 h-3" /> {t("admin_orders.modal.view_proof")}</a>
                          )}
                        </div>
                      ))}

                      <div className="flex justify-between items-center p-3 text-sm border-t border-stone-200 mt-2">
                        <span className="font-medium text-stone-600">Total Paid: {formatPrice(totalPaid)}</span>
                        <span className={`font-semibold ${remaining <= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>Remaining: {formatPrice(remaining)}</span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-stone-400 italic mb-4">{t("admin_orders.modal.no_payments")}</p>
                  )}

                  {/* Manual payment recording removed per user request */}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>



      </div>
    </AdminLayout>
  );
}
